import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Schema } from '../../../../core/_models/schema';
import { AbstractControlValueAccessor } from '../../../../shared/abstract_control_value_accessor.abstract';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';

import { TranslatePipe } from '@ngx-translate/core';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';

@Component({
  selector: 'app-password-input-confirmation',
  templateUrl: './password-input-confirmation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PasswordInputConfirmationComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordInputConfirmationComponent), multi: true },
  ],
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    AppFocusableDirective,
    InputErrorsComponent,
  ],
})
export class PasswordInputConfirmationComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  private cdr = inject(ChangeDetectorRef);


  public readonly controlName = input<string>(undefined!);
  public readonly properties = input<Schema>(undefined!);

  // TODO migrate manually to signals
  @Input() public errors: any;
  public readonly errorsChange = output<any>();

  public readonly readonly = input(false);
  public readonly submitted = input(false);

  public formControl = new UntypedFormControl(null);
  public confirmPasswordFormControl = new UntypedFormControl('');

  private formSub: Subscription;
  private confirmPasswordFormControlSub: Subscription;

  public ngOnInit(): void {
    this.formSub = this.formControl.valueChanges.subscribe(() => this.formControlChange());
    this.confirmPasswordFormControlSub = this.confirmPasswordFormControl.valueChanges
      .subscribe(() => this.formControlChange());
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(obj: any): void {
    this.formControl.patchValue(obj);
    this.cdr.markForCheck();
  }

  public validate(c: AbstractControl): ValidationErrors | any {

    if (c.value.length < environment.passwordMinLength) {
      return this.setErrors(c, {
        'error': 'password_too_short',
        'property': 'password',
        'min_length': environment.passwordMinLength,
      });
    }

    if (c.value !== this.confirmPasswordFormControl.value) {
      return this.setErrors(c, { 'error': 'password_mismatch', 'property': 'password' });
    }

    return this.setErrors(c, null);
  }

  public setErrors(formControl, errors): void {
    formControl.errors = errors;
    this.errors = errors;
    if (errors) {
      this.errorsChange.emit(this.errors);
    }
    return errors;
  }

  private formControlChange(): void {
    this.onTouched();
    this.onChange(this.formControl.value);
  }
}
